/**
 * Home's reads (plan docs/plans/lms-plan.md 7.1, 7.2, 9.4, Appendix A; 18.16,
 * 18.22, 18.24; WP-41).
 *
 * - The to-do: GET /me/assignments?status=open (open ones by due date, the
 *   order 7.1 asks for; each with source_label, due_state and the course
 *   summary, WP-31), and for each of the first MAX_PROGRESS_LOOKUPS the
 *   bookmark (GET /me/progress/:courseId, WP-40's api/progress.ts). A row with
 *   a bookmark is started: its button reads Continue, and it is listed under
 *   "In progress" (7.1: "attempts and slide bookmarks"). A bookmark that
 *   cannot be read (a course retired since) counts as none.
 * - Recent: GET /me/completions?limit=5 (7.1: the last five completions),
 *   WP-40's api/completions.ts listMyCompletions.
 * - The onboarding card: GET /me/onboarding (18.24), the active run or null.
 *
 * There is no route that lists every bookmark or every in-progress attempt
 * (Appendix A reads a bookmark per course), so "In progress" covers the
 * courses the person is assigned; a course started voluntarily from the
 * catalog shows its Continue on the Courses tab instead.
 */
import { listMyAssignments } from '../../api/me';
import type { MyAssignmentItem } from '../../api/me';
import { getMyProgress } from '../../api/progress';
import type { LmsProgress } from '../../api/progress';
import { listMyCompletions } from '../../api/completions';
import type { TranscriptItem } from '../../api/completions';

/** Bookmarks read per Home load: one request each, so the count is bounded. */
export const MAX_PROGRESS_LOOKUPS = 25;

/** 7.1: "Recent" is the last five completions. */
export const RECENT_COUNT = 5;

/** Appendix A: a page is at most 200 rows. */
export const TODO_PAGE_LIMIT = 200;

export interface TodoRow {
  assignment: MyAssignmentItem;
  progress: LmsProgress | null;
  /** A bookmark exists: slides seen, the reading opened or done (7.2). */
  started: boolean;
}

export interface TodoData {
  rows: TodoRow[];
  /** More open assignments than one page holds. */
  hasMore: boolean;
}

/** Whether a bookmark shows the course was started. */
export function isStarted(progress: LmsProgress | null | undefined): boolean {
  if (!progress) return false;
  const seen = Array.isArray(progress.slides_seen) ? progress.slides_seen.length : 0;
  return seen > 0 || progress.slides_done === true || progress.reading_done === true || !!progress.reading_opened_at;
}

/** The player route for a course (18.3: /lms/courses/:courseId/learn). */
export function learnPath(courseId: string): string {
  return `/lms/courses/${encodeURIComponent(courseId)}/learn`;
}

/** The course page (18.3: /lms/courses/:courseId). */
export function coursePath(courseId: string): string {
  return `/lms/courses/${encodeURIComponent(courseId)}`;
}

export async function loadTodo(): Promise<TodoData> {
  const page = await listMyAssignments({ status: 'open', limit: TODO_PAGE_LIMIT });
  const items = Array.isArray(page?.items) ? page.items.filter((a) => a.status === 'open') : [];
  const looked = items.slice(0, MAX_PROGRESS_LOOKUPS);
  const progress = await Promise.all(looked.map((a) => getMyProgress(a.course_id).catch(() => null)));
  const rows = items.map((assignment, i) => {
    const p = i < progress.length ? progress[i] : null;
    return { assignment, progress: p, started: isStarted(p) };
  });
  return { rows, hasMore: page?.page?.hasMore === true };
}

export async function loadRecent(): Promise<TranscriptItem[]> {
  const page = await listMyCompletions({ limit: RECENT_COUNT });
  return Array.isArray(page?.items) ? page.items.slice(0, RECENT_COUNT) : [];
}
